import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row, Spin, Tooltip as AntTooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Cell,
    Legend,
    Pie,
    PieChart as RPieChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';
import * as SERVICE from '../../../constants/service.types';
import { socketEmitEvent } from '../../../redux/actions';

const CustomCameraTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const total = payload[0].payload.total || 1;
        const percent = ((payload[0].value / total) * 100).toFixed(2);
        return (
            <div
                style={{
                    backgroundColor: '#000',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #333',
                }}
            >
                <span style={{ color: payload[0].payload.color, fontWeight: 'bold' }}>
                    {`${payload[0].name}: ${payload[0].value} (${percent}%)`}
                </span>
            </div>
        );
    }
    return null;
};

const renderLegend = (props) => {
    const { payload } = props;
    return (
        <ul className="legend-wrapper" style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
            {payload?.map((entry, index) => {
                const itemData = entry.payload || {};
                const name = itemData.name || entry.value || '';
                const count = itemData.value || 0;
                const total = itemData.total || 1;
                const percent = ((count / total) * 100).toFixed(2);
                return (
                    <li
                        className="item-legend"
                        style={{ color: entry.color, marginBottom: '6px', fontSize: '13px', display: 'flex', alignItems: 'center' }}
                        key={`item-${index}`}
                    >
                        <span
                            className="bullet"
                            style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                background: entry.color,
                                marginRight: '8px',
                                borderRadius: '2px',
                            }}
                        ></span>
                        {`${name} - ${count} (${percent}%)`}
                    </li>
                );
            })}
        </ul>
    );
};


const CameraStatusStatistic = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isAuth, namespace, cameras } = useSelector((state) => state);
    const [loading, setLoading] = useState(false);
    const loadingTimerRef = useRef(null);

    const handleReload = useCallback(() => {
        if (isAuth === 1 && namespace) {
            setLoading(true);
            if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
            loadingTimerRef.current = setTimeout(() => {
                setLoading(false);
            }, 3000);

            const reqTime = new Date().getTime();
            const data = {
                deviceType: 'camera',
                timeRequest: reqTime,
                namespace,
                isCache: 0,
            };
            const dataObj = {
                appServiceType: SERVICE.STATISTIC_DEVICE_STATUS,
                requestId: reqTime,
                body: JSON.stringify(data),
            };
            console.log("🚀 ~ CameraStatusStatistic ~ dataObj:", dataObj);

            dispatch(socketEmitEvent(dataObj));
        }
    }, [dispatch, isAuth, namespace]);

    useEffect(() => {
        handleReload();
    }, [handleReload]);

    useEffect(() => {
        if (cameras) {
            setLoading(false);
            if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
        }
    }, [cameras]);

    const totalCamera = useMemo(() => {
        return (cameras?.numberOnline || 0) + (cameras?.numberOffline || 0) + (cameras?.numberMaintain || 0);
    }, [cameras]);

    const cameraData = useMemo(() => {
        const total = totalCamera || 1;
        return [
            {
                name: t('Online'),
                value: cameras?.numberOnline || 0,
                color: '#0E9347', // Xanh lá
                total,
            },
            {
                name: t('Offline'),
                value: cameras?.numberOffline || 0,
                color: '#ef4444', // Đỏ
                total,
            },
            {
                name: t('Maintain'),
                value: cameras?.numberMaintain || 0,
                color: '#f59e0b', // Vàng / Cam
                total,
            },
        ];
    }, [cameras, totalCamera, t]);

    if (namespace !== "hdbank.com.vn") return null;

    return (
        <div className="chart-item content-center" style={{ width: '50%', position: 'relative' }}>
            <AntTooltip title={t('Reload')}>
                <ReloadOutlined
                    onClick={handleReload}
                    style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        color: '#8c8c8c',
                        fontSize: '16px',
                        cursor: 'pointer',
                        zIndex: 10,
                    }}
                />
            </AntTooltip>
            <div className="chart-title" style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '16px', color: '#fff', marginBottom: '16px' }}>
                {t('Camera')}
            </div>
            {loading ? (
                <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin size="default" />
                </div>
            ) : (
                <Row style={{ width: '100%', alignItems: 'center' }}>
                    <Col span={6}>
                        <div className="extra-info" style={{ textAlign: 'center' }}>
                            <div style={{ color: '#aaa', fontSize: '14px', marginBottom: '8px' }}>{t('Camera Quantity')}</div>
                            <div className="total-device" style={{ fontSize: '36px', fontWeight: 'bold', color: '#fff' }}>
                                {totalCamera}
                            </div>
                        </div>
                    </Col>
                    <Col span={18}>
                        <ResponsiveContainer width="100%" height={260}>
                            <RPieChart width={500} height={260}>
                                <Pie
                                    dataKey="value"
                                    isAnimationActive={false}
                                    data={cameraData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    innerRadius={45}
                                    stroke="none"
                                >
                                    {cameraData?.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={entry.color}
                                            stroke="none"
                                        />
                                    ))}
                                </Pie>

                                <Tooltip content={<CustomCameraTooltip />} />
                                <Legend
                                    layout="vertical"
                                    align="left"
                                    verticalAlign="middle"
                                    content={renderLegend}
                                />
                            </RPieChart>
                        </ResponsiveContainer>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default CameraStatusStatistic;